// Utilidades para optimización SEO

export const config = {
    siteUrl: import.meta.env.VITE_SITE_URL || 'http://localhost:3000',
    isDevelopment: import.meta.env.DEV
};

export const generateStructuredData = {
    // Datos estructurados para la página principal
    homepage: (listings) => ({
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        "name": "Estéticas de Argentina - Directorio de Centros de Belleza",
        "description": "Directorio completo de centros de estética, medicina estética, spa y salones de belleza en Argentina",
        "url": "https://esteticasdeargentina.com",
        "mainEntity": {
            "@type": "ItemList",
            "numberOfItems": listings.length,
            "itemListElement": listings.slice(0, 10).map((listing, index) => ({
                "@type": "ListItem",
                "position": index + 1,
                "item": {
                    "@type": "MedicalBusiness",
                    "name": listing.name,
                    "description": listing.description,
                    "url": `https://esteticasdeargentina.com/centro/${listing.id}`,
                    "address": {
                        "@type": "PostalAddress",
                        "addressLocality": listing.city,
                        "addressRegion": listing.province,
                        "addressCountry": "AR",
                        "streetAddress": listing.contact?.address
                    },
                    "telephone": listing.contact?.phone,
                    "email": listing.contact?.email,
                    "openingHours": listing.hours,
                    "aggregateRating": {
                        "@type": "AggregateRating",
                        "ratingValue": listing.rating,
                        "bestRating": 5,
                        "worstRating": 1
                    },
                    "medicalSpecialty": listing.specialties
                }
            }))
        }
    }),

    // Datos estructurados para página de centro individual
    centerDetail: (listing) => ({
        "@context": "https://schema.org",
        "@type": "MedicalBusiness",
        "name": listing.name,
        "description": listing.description,
        "url": `https://esteticasdeargentina.com/centro/${listing.id}`,
        "image": listing.images,
        "address": {
            "@type": "PostalAddress",
            "addressLocality": listing.city,
            "addressRegion": listing.province,
            "addressCountry": "AR",
            "streetAddress": listing.contact?.address
        },
        "telephone": listing.contact?.phone,
        "email": listing.contact?.email,
        "openingHours": listing.hours,
        "aggregateRating": {
            "@type": "AggregateRating",
            "ratingValue": listing.rating,
            "bestRating": 5,
            "worstRating": 1,
            "ratingCount": 50
        },
        "medicalSpecialty": listing.specialties,
        "hasMap": listing.contact?.address ? `https://maps.google.com/maps?q=${encodeURIComponent(listing.contact.address)}` : null,
        "priceRange": listing.tier === 1 ? "$$$" : listing.tier === 2 ? "$$" : "$",
        "paymentAccepted": ["Cash", "Credit Card"],
        "sameAs": [
            listing.contact?.website,
            listing.contact?.whatsapp
        ].filter(Boolean)
    }),

    // Datos estructurados para páginas de categoría
    categoryPage: (category, listings) => ({
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        "name": `${category} en Argentina`,
        "description": `Encuentra los mejores centros de ${category.toLowerCase()} en Argentina`,
        "url": `https://esteticasdeargentina.com/categoria/${category}`,
        "mainEntity": {
            "@type": "ItemList",
            "numberOfItems": listings.length,
            "itemListElement": listings.map((listing, index) => ({
                "@type": "ListItem",
                "position": index + 1,
                "item": {
                    "@type": "MedicalBusiness",
                    "name": listing.name,
                    "url": `https://esteticasdeargentina.com/centro/${listing.id}`,
                    "address": {
                        "@type": "PostalAddress",
                        "addressLocality": listing.city,
                        "addressRegion": listing.province
                    }
                }
            }))
        }
    }),

    // Datos estructurados para páginas de provincia
    provincePage: (province, listings) => ({
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        "name": `Centros de Estética en ${province}`,
        "description": `Directorio de centros de estética, medicina estética y spa en ${province}`,
        "url": `https://esteticasdeargentina.com/provincia/${province}`,
        "mainEntity": {
            "@type": "ItemList",
            "numberOfItems": listings.length,
            "itemListElement": listings.map((listing, index) => ({
                "@type": "ListItem",
                "position": index + 1,
                "item": {
                    "@type": "MedicalBusiness",
                    "name": listing.name,
                    "url": `https://esteticasdeargentina.com/centro/${listing.id}`,
                    "address": {
                        "@type": "PostalAddress",
                        "addressLocality": listing.city,
                        "addressRegion": listing.province
                    }
                }
            }))
        }
    })
};

// Generar URLs amigables para SEO
export const generateSlug = (text) => {
    return text
        .toLowerCase()
        .replace(/[áàäâ]/g, 'a')
        .replace(/[éèëê]/g, 'e')
        .replace(/[íìïî]/g, 'i')
        .replace(/[óòöô]/g, 'o')
        .replace(/[úùüû]/g, 'u')
        .replace(/ñ/g, 'n')
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .trim('-');
};

// Generar meta descriptions dinámicas
export const generateMetaDescription = {
    center: (listing) =>
        `${listing.name} en ${listing.city}, ${listing.province}. ${listing.specialties?.slice(0, 3).join(', ')}. ${listing.experience} de experiencia. Rating ${listing.rating}/5.`,

    category: (category, count, province = null) => {
        const location = province ? ` en ${province}` : ' en Argentina';
        return `Encuentra los mejores ${category.toLowerCase()}${location}. ${count} centros verificados con reseñas reales y información completa.`;
    },

    province: (province, count) =>
        `Centros de estética en ${province}. ${count} profesionales verificados. Medicina estética, spa, salones de belleza y más.`
};

// Generar breadcrumbs estructurados
export const generateBreadcrumbSchema = (breadcrumbs) => ({
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": breadcrumbs.map((crumb, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "name": crumb.name,
        "item": `https://esteticasdeargentina.com${crumb.url}`
    }))
});

// Optimizar títulos para SEO
export const generatePageTitle = {
    center: (listing) =>
        `${listing.name} - ${listing.city}, ${listing.province} | Estéticas de Argentina`,

    category: (category, province = null) => {
        const location = province ? ` en ${province}` : '';
        return `${category}${location} | Estéticas de Argentina`;
    },

    province: (province) =>
        `Centros de Estética en ${province} | Estéticas de Argentina`
};

export default {
    config,
    generateStructuredData,
    generateSlug,
    generateMetaDescription,
    generateBreadcrumbSchema,
    generatePageTitle
};